import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaChevronDown, FaChevronRight, FaCloud, FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa'
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import gateStyles from '../fairclaimApiView/styles.module.scss'
import { GateControls } from '../fairclaimApiView/gateControls'
import { JsonViewer } from './jsonViewer'
import { ExportModal } from './exportModal'
import {
    BASE_URL,
    DEFAULT_OPTICS_FILTERS,
    DeliveryRow,
    DOCUMENT_ROLE_LABELS,
    EXPORT_FIELDS,
    ListFilter,
    OpticsFilters,
    OpticsView,
    RANGE_PRESETS,
    REASON_LABELS,
    STATUS_LABELS,
    StatsResponse,
    SummaryResponse,
    buildReportCsv,
    describeFilters,
    downloadCsvFile,
    extractDocumentFailures,
    extractPayloadAgreements,
    extractPayloadDocuments,
    failureForDocument,
    filtersEqual,
    formatDateTime,
    formatDateUk,
    formatDay,
} from './shared'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const PAGE_SIZE = 25

const REASONS_OPEN_KEY = 'ffjOptics.reasonsOpen'

interface DetailState {
    loading: boolean
    error?: string
    view: OpticsView
    data?: any
}

/** Record refs in the URL look like "accepted-9" / "rejected-711". */
const parseRecordParam = (value: string | null): { outcome: OpticsView; id: number } | null => {
    const match = value?.match(/^(accepted|rejected)-(\d+)$/)
    if (!match) return null
    return { outcome: match[1] as OpticsView, id: Number(match[2]) }
}

export const OpticsTab = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const viewParam = searchParams.get('view')
    const urlView: ListFilter = viewParam === 'accepted' || viewParam === 'rejected' ? viewParam : 'all'
    const recordParam = searchParams.get('record')
    const record = useMemo(() => parseRecordParam(recordParam), [recordParam])

    // Filters only take effect when applied, so the list, summary and report
    // always describe the same result set the user asked for.
    const [applied, setApplied] = useState<OpticsFilters>({ ...DEFAULT_OPTICS_FILTERS, view: urlView })
    const [draft, setDraft] = useState<OpticsFilters>({ ...DEFAULT_OPTICS_FILTERS, view: urlView })
    const filter = applied.view

    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [summary, setSummary] = useState<SummaryResponse | null>(null)
    const [summaryLoading, setSummaryLoading] = useState(false)
    const [rows, setRows] = useState<DeliveryRow[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [baseline, setBaseline] = useState<string | null>(null)
    const [listLoading, setListLoading] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const [detail, setDetail] = useState<DetailState | null>(null)
    const [exporting, setExporting] = useState(false)
    const [reportBusy, setReportBusy] = useState(false)
    const [purging, setPurging] = useState(false)
    const [refreshNonce, setRefreshNonce] = useState(0)
    const [reasonsOpen, setReasonsOpen] = useState(
        () => window.localStorage.getItem(REASONS_OPEN_KEY) !== 'closed'
    )

    const includeTest = applied.includeTest
    const includeHistorical = applied.includeHistorical
    const hasPendingChanges = !filtersEqual(draft, applied)

    const toggleReasons = useCallback(() => {
        setReasonsOpen((open) => {
            window.localStorage.setItem(REASONS_OPEN_KEY, open ? 'closed' : 'open')
            return !open
        })
    }, [])

    // Deep links from the overview tab set ?view=accepted|rejected.
    useEffect(() => {
        setApplied((current) => (current.view === urlView ? current : { ...current, view: urlView }))
        setDraft((current) => (current.view === urlView ? current : { ...current, view: urlView }))
    }, [urlView])

    const patchDraft = useCallback((patch: Partial<OpticsFilters>) => {
        setDraft((current) => ({ ...current, ...patch }))
    }, [])

    /** Commit the draft (or an immediate patch of it) and restart at page 1. */
    const applyFilters = useCallback(
        (patch?: Partial<OpticsFilters>) => {
            const next = { ...draft, ...patch }
            setDraft(next)
            setApplied(next)
            setPage(1)
            setSearchParams(next.view === 'all' ? {} : { view: next.view })
        },
        [draft, setSearchParams]
    )

    const resetFilters = useCallback(() => {
        setDraft({ ...DEFAULT_OPTICS_FILTERS })
        setApplied({ ...DEFAULT_OPTICS_FILTERS })
        setPage(1)
        setSearchParams({})
    }, [setSearchParams])

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-api-stats`, {
                    days: 30,
                    includeHistorical,
                })
                if (response.data?.success) setStats(response.data)
            } catch {
                /* stat cards stay empty */
            }
        }
        fetchStats()
    }, [includeHistorical, refreshNonce])

    useEffect(() => {
        let cancelled = false
        const fetchRows = async () => {
            setListLoading(true)
            setListError(null)
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-deliveries`, {
                    view: applied.view,
                    search: applied.search.trim(),
                    includeTest: applied.includeTest,
                    includeHistorical: applied.includeHistorical,
                    dateFrom: applied.dateFrom || undefined,
                    dateTo: applied.dateTo || undefined,
                    sort: applied.sort,
                    page,
                    pageSize: PAGE_SIZE,
                })
                if (cancelled) return
                if (!response.data?.success) throw new Error(response.data?.message || 'List request failed')
                setRows(response.data.rows)
                setTotal(response.data.total)
                setBaseline(response.data.baseline ?? null)
            } catch (error: any) {
                if (!cancelled) setListError(error?.response?.data?.message || error?.message || 'Could not load the list')
            } finally {
                if (!cancelled) setListLoading(false)
            }
        }
        fetchRows()
        return () => {
            cancelled = true
        }
    }, [applied, page, refreshNonce])

    // Deliberately not tied to `page` — paging the table must not re-run the
    // aggregates for the whole filtered set.
    useEffect(() => {
        let cancelled = false
        const fetchSummary = async () => {
            setSummaryLoading(true)
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-deliveries-summary`, {
                    view: applied.view,
                    search: applied.search.trim(),
                    includeTest: applied.includeTest,
                    includeHistorical: applied.includeHistorical,
                    dateFrom: applied.dateFrom || undefined,
                    dateTo: applied.dateTo || undefined,
                })
                if (cancelled) return
                setSummary(response.data?.success ? response.data : null)
            } catch {
                if (!cancelled) setSummary(null)
            } finally {
                if (!cancelled) setSummaryLoading(false)
            }
        }
        fetchSummary()
        return () => {
            cancelled = true
        }
    }, [applied, refreshNonce])

    const openRecord = useCallback(
        (outcome: OpticsView, id: number) => {
            setSearchParams({ view: filter, record: `${outcome}-${id}` })
        },
        [setSearchParams, filter]
    )

    const closeRecord = useCallback(() => {
        setSearchParams({ view: filter })
    }, [setSearchParams, filter])

    useEffect(() => {
        if (!record) {
            setDetail(null)
            return
        }
        let cancelled = false
        const fetchDetail = async () => {
            setDetail({ loading: true, view: record.outcome })
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-delivery-detail`, {
                    view: record.outcome,
                    id: record.id,
                })
                if (cancelled) return
                if (!response.data?.success) throw new Error(response.data?.message || 'Detail request failed')
                setDetail({ loading: false, view: record.outcome, data: response.data })
            } catch (error: any) {
                if (!cancelled)
                    setDetail({
                        loading: false,
                        view: record.outcome,
                        error: error?.response?.data?.message || error?.message || 'Could not load the record',
                    })
            }
        }
        fetchDetail()
        return () => {
            cancelled = true
        }
    }, [record])

    const purgeRejections = () => {
        Confirm.show(
            'Delete ALL rejected API data?',
            'Every rejection FFJ has ever sent to the API — live, test and pre-reset history — will be ' +
                'permanently deleted so the reset starts completely fresh. Accepted deliveries and stored client ' +
                'records are NOT affected. This cannot be undone.',
            'Delete everything',
            'Cancel',
            async () => {
                setPurging(true)
                try {
                    const response = await api.post(`${BASE_URL}/api/ffj-rejections-purge`, { confirm: true })
                    if (!response.data?.success) throw new Error(response.data?.message || 'Purge request failed')
                    Report.success(
                        'Rejected data deleted',
                        `${response.data.deleted} rejection record${response.data.deleted === 1 ? '' : 's'} permanently deleted. The API optics now start fresh.`,
                        'Okay'
                    )
                    setRefreshNonce((n) => n + 1)
                } catch (error: any) {
                    Report.failure(
                        'Delete failed',
                        error?.response?.data?.message || error?.message || 'Could not delete the rejected data.',
                        'Okay'
                    )
                } finally {
                    setPurging(false)
                }
            },
            () => undefined,
            { titleColor: '#c62828', okButtonBackground: '#c62828', width: '380px' }
        )
    }

    /**
     * Report for exactly the applied filters: the summary already on screen
     * plus every matching delivery row (un-paginated).
     */
    const downloadReport = async () => {
        setReportBusy(true)
        try {
            const response = await api.post(`${BASE_URL}/api/ffj-deliveries-export`, {
                view: applied.view,
                search: applied.search.trim(),
                includeTest: applied.includeTest,
                includeHistorical: applied.includeHistorical,
                dateFrom: applied.dateFrom || undefined,
                dateTo: applied.dateTo || undefined,
            })
            if (!response.data?.success) throw new Error(response.data?.message || 'Report request failed')
            const reportRows: DeliveryRow[] = response.data.rows ?? []
            const csv = buildReportCsv({
                brand: 'FFJ',
                filters: applied,
                summary: summary?.summary ?? null,
                daily: summary?.daily ?? [],
                rows: reportRows,
                fields: EXPORT_FIELDS.filter((f) => f.defaultOn),
                truncated: response.data.truncated === true,
            })
            const suffix =
                applied.dateFrom || applied.dateTo
                    ? `${applied.dateFrom || 'start'}_to_${applied.dateTo || 'latest'}`
                    : new Date().toISOString().slice(0, 10)
            downloadCsvFile(csv, `ffj-api-report-${suffix}.csv`)
            Report.success(
                'Report downloaded',
                `${reportRows.length} record${reportRows.length === 1 ? '' : 's'} included` +
                    (response.data.truncated ? ' (capped at the 50,000-row export limit).' : '.'),
                'Okay'
            )
        } catch (error: any) {
            Report.failure(
                'Report failed',
                error?.response?.data?.message || error?.message || 'Could not build the report.',
                'Okay'
            )
        } finally {
            setReportBusy(false)
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const reasonTotal = useMemo(
        () => (stats?.rejection_reasons ?? []).reduce((n, r) => n + r.count, 0),
        [stats]
    )

    const summaryChart = useMemo(() => {
        const points = summary?.daily ?? []
        return {
            data: {
                labels: points.map((p) => p.day),
                datasets: [
                    {
                        label: 'Received',
                        data: points.map((p) => p.received),
                        borderColor: '#64748b',
                        backgroundColor: 'rgba(100, 116, 139, 0.12)',
                        fill: true,
                        tension: 0.3,
                    },
                    {
                        label: 'Accepted',
                        data: points.map((p) => p.accepted),
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(46, 125, 50, 0.12)',
                        fill: true,
                        tension: 0.3,
                    },
                    {
                        label: 'Rejected',
                        data: points.map((p) => p.rejected),
                        borderColor: '#c62828',
                        backgroundColor: 'rgba(198, 40, 40, 0.12)',
                        fill: true,
                        tension: 0.3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                plugins: {
                    legend: { position: 'bottom' as const, labels: { boxWidth: 12, usePointStyle: true } },
                    tooltip: {
                        callbacks: {
                            title: (items: any[]) => (items[0] ? formatDay(String(items[0].label)) : ''),
                        },
                    },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
                    y: { beginAtZero: true, ticks: { precision: 0 } },
                },
            },
        }
    }, [summary])

    const summaryTotals = summary?.summary ?? null
    const summaryWindow = useMemo(() => {
        const from = summary?.range?.from
        const to = summary?.range?.to
        if (!from || !to) return null
        return from === to ? formatDay(from) : `${formatDay(from)} to ${formatDay(to)}`
    }, [summary])

    return (
        <div>
            <GateControls brand="ffj" brandLabel="FFJ Data Optics" styles={gateStyles} />
            {baseline && !includeHistorical && (
                <div className={styles.baselineBanner}>
                    <span>
                        <strong>Fresh start:</strong> showing activity since {formatDateTime(baseline)}. Everything
                        before that was the integration testing phase — it has been reported back to FFJ and is
                        hidden here so the numbers reflect live behaviour.
                    </span>
                    <button onClick={() => applyFilters({ includeHistorical: true })}>Show testing history</button>
                </div>
            )}
            {includeHistorical && (
                <div className={styles.baselineBannerHistorical}>
                    <span>
                        <strong>Including testing history:</strong> stats and the list below include the pre-reset
                        integration testing traffic.
                    </span>
                    <button onClick={() => applyFilters({ includeHistorical: false })}>Back to fresh view</button>
                </div>
            )}
            <div className={styles.statCards}>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{stats?.totals.received ?? '—'}</span>
                    <span className={styles.statLabel}>Requests received</span>
                </div>
                <div className={styles.statCard}>
                    <span className={`${styles.statValue} ${styles.statGood}`}>{stats?.totals.accepted ?? '—'}</span>
                    <span className={styles.statLabel}>Clients accepted</span>
                </div>
                <div className={styles.statCard}>
                    <span className={`${styles.statValue} ${styles.statBad}`}>{stats?.totals.rejected ?? '—'}</span>
                    <span className={styles.statLabel}>Rejected</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>
                        {stats?.totals.acceptance_rate != null ? `${stats.totals.acceptance_rate}%` : '—'}
                    </span>
                    <span className={styles.statLabel}>Acceptance rate</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{stats?.totals.accepted_agreements ?? '—'}</span>
                    <span className={styles.statLabel}>Cases created</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>
                        {stats ? stats.totals.test_deliveries + stats.totals.test_rejections : '—'}
                    </span>
                    <span className={styles.statLabel}>Test deliveries</span>
                </div>
            </div>

            {stats && stats.rejection_reasons.length > 0 && (
                <div className={styles.reasonsCard}>
                    <button
                        type="button"
                        className={styles.collapseHeader}
                        onClick={toggleReasons}
                        aria-expanded={reasonsOpen}
                    >
                        {reasonsOpen ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                        <h4>Rejection reasons</h4>
                        <span className={styles.collapseMeta}>
                            {stats.rejection_reasons.length} categor
                            {stats.rejection_reasons.length === 1 ? 'y' : 'ies'} · {reasonTotal} record
                            {reasonTotal === 1 ? '' : 's'}
                        </span>
                        <span className={styles.collapseHint}>{reasonsOpen ? 'Hide' : 'Show'}</span>
                    </button>
                    {reasonsOpen &&
                        stats.rejection_reasons.map((r) => (
                            <div key={r.category} className={styles.reasonRow}>
                                <span className={styles.reasonLabel}>{REASON_LABELS[r.category] ?? r.category}</span>
                                <div className={styles.reasonBarTrack}>
                                    <div
                                        className={styles.reasonBarFill}
                                        style={{
                                            width: `${reasonTotal ? Math.max(2, (r.count / reasonTotal) * 100) : 0}%`,
                                        }}
                                    />
                                </div>
                                <span className={styles.reasonCount}>
                                    {r.count} ({reasonTotal ? Math.round((r.count / reasonTotal) * 100) : 0}%)
                                </span>
                            </div>
                        ))}
                </div>
            )}

            <div className={styles.filterPanel}>
                <div className={styles.filterPanelHeader}>
                    <h4>Filter deliveries</h4>
                    <span className={styles.footnote}>
                        Pick a range, then apply. The stats, chart and report below all describe exactly what the
                        filters return.
                    </span>
                </div>

                <div className={styles.presetRow}>
                    {RANGE_PRESETS.map((preset) => {
                        const range = preset.resolve()
                        const active = draft.dateFrom === range.dateFrom && draft.dateTo === range.dateTo
                        return (
                            <button
                                key={preset.id}
                                type="button"
                                className={active ? styles.presetChipActive : styles.presetChip}
                                onClick={() => patchDraft(range)}
                            >
                                {preset.label}
                            </button>
                        )
                    })}
                    <button
                        type="button"
                        className={styles.presetChip}
                        onClick={() => patchDraft({ dateFrom: '', dateTo: '' })}
                    >
                        Clear dates
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <label className={styles.filterField}>
                        <span>Outcome</span>
                        <select
                            value={draft.view}
                            onChange={(e) => patchDraft({ view: e.target.value as ListFilter })}
                        >
                            <option value="all">All deliveries</option>
                            <option value="accepted">Accepted only</option>
                            <option value="rejected">Rejected only</option>
                        </select>
                    </label>
                    <label className={styles.filterField}>
                        <span>From</span>
                        <input
                            type="date"
                            value={draft.dateFrom}
                            onChange={(e) => patchDraft({ dateFrom: e.target.value })}
                        />
                    </label>
                    <label className={styles.filterField}>
                        <span>To</span>
                        <input
                            type="date"
                            value={draft.dateTo}
                            onChange={(e) => patchDraft({ dateTo: e.target.value })}
                        />
                    </label>
                    <label className={styles.filterField}>
                        <span>Sort</span>
                        <select
                            value={draft.sort}
                            onChange={(e) => patchDraft({ sort: e.target.value as 'newest' | 'oldest' })}
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                        </select>
                    </label>
                    <label className={`${styles.filterField} ${styles.filterFieldWide}`}>
                        <span>Search</span>
                        <input
                            placeholder="Client name, sender, client ref, email, delivery id…"
                            value={draft.search}
                            onChange={(e) => patchDraft({ search: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') applyFilters()
                            }}
                        />
                    </label>
                    <div className={styles.filterToggles}>
                        <label>
                            <input
                                type="checkbox"
                                checked={draft.includeTest}
                                onChange={(e) => patchDraft({ includeTest: e.target.checked })}
                            />
                            Include test deliveries
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={draft.includeHistorical}
                                onChange={(e) => patchDraft({ includeHistorical: e.target.checked })}
                            />
                            Include pre-reset history
                        </label>
                    </div>
                </div>

                <div className={styles.filterActions}>
                    <span className={hasPendingChanges ? styles.pendingHint : styles.appliedHint}>
                        {hasPendingChanges ? 'Filters changed — not applied yet' : describeFilters(applied)}
                    </span>
                    <div className={styles.filterButtons}>
                        <button
                            type="button"
                            className={styles.applyButton}
                            onClick={() => applyFilters()}
                            disabled={listLoading && !hasPendingChanges}
                        >
                            Apply filters
                        </button>
                        <button type="button" className={styles.secondaryButton} onClick={resetFilters}>
                            Reset
                        </button>
                        <button
                            className={styles.refreshButton}
                            onClick={() => setRefreshNonce((n) => n + 1)}
                            disabled={listLoading}
                            title="Reload the stats and delivery list with the latest data"
                        >
                            <FaSyncAlt className={listLoading ? styles.refreshSpinning : undefined} aria-hidden="true" />
                            {listLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                        <button
                            className={styles.exportButton}
                            onClick={downloadReport}
                            disabled={reportBusy}
                            title="Download a CSV report for the filtered set: totals, a per-day breakdown and every matching delivery"
                        >
                            {reportBusy ? 'Building…' : 'Download report'}
                        </button>
                        <button className={styles.secondaryButton} onClick={() => setExporting(true)}>
                            Export CSV…
                        </button>
                        <button
                            className={styles.dangerButton}
                            onClick={purgeRejections}
                            disabled={purging}
                            title="Permanently delete every rejection recorded from the FFJ API (live, test and pre-reset history) so the data reset starts fresh. Accepted deliveries and client records are untouched."
                        >
                            {purging ? 'Deleting…' : 'Delete Rejected Data'}
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.resultSummaryCard}>
                <div className={styles.chartHeader}>
                    <h4>Filtered result set</h4>
                    <span className={styles.footnote}>
                        {summaryLoading
                            ? 'Calculating…'
                            : summaryWindow
                              ? `${summaryWindow} · ${total} record${total === 1 ? '' : 's'}`
                              : 'No deliveries match these filters'}
                    </span>
                </div>
                <div className={styles.statCards}>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{summaryTotals?.received ?? '—'}</span>
                        <span className={styles.statLabel}>Requests sent</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={`${styles.statValue} ${styles.statGood}`}>
                            {summaryTotals?.accepted ?? '—'}
                        </span>
                        <span className={styles.statLabel}>Accepted</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={`${styles.statValue} ${styles.statBad}`}>
                            {summaryTotals?.rejected ?? '—'}
                        </span>
                        <span className={styles.statLabel}>Rejected</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{summaryTotals?.failed ?? '—'}</span>
                        <span className={styles.statLabel}>Failed (internal)</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>
                            {summaryTotals?.acceptance_rate != null ? `${summaryTotals.acceptance_rate}%` : '—'}
                        </span>
                        <span className={styles.statLabel}>Acceptance rate</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{summaryTotals?.accepted_agreements ?? '—'}</span>
                        <span className={styles.statLabel}>Cases created (payable)</span>
                    </div>
                </div>
                {(summary?.daily?.length ?? 0) > 0 && (
                    <div className={styles.chartWrap}>
                        <Line data={summaryChart.data} options={summaryChart.options} />
                    </div>
                )}
            </div>

            {listError ? (
                <p className={styles.errorText}>{listError}</p>
            ) : (
                <div className={listLoading ? styles.tableLoading : undefined}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Received</th>
                                <th>Sender</th>
                                <th>Client</th>
                                <th>Client ref</th>
                                <th>Email</th>
                                <th>Agreements</th>
                                <th>Outcome</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const outcome = (row.outcome ?? 'accepted') as OpticsView
                                return (
                                    <tr
                                        key={`${outcome}-${row.id}`}
                                        onClick={() => openRecord(outcome, row.id)}
                                        className={styles.clickableRow}
                                    >
                                        <td>{formatDateTime(row.created_at)}</td>
                                        <td>{row.sender_name || '—'}</td>
                                        <td>{row.client_name || '—'}</td>
                                        <td>{row.consumer_client_ref ?? '—'}</td>
                                        <td>{row.consumer_email ?? '—'}</td>
                                        <td>{row.agreement_count}</td>
                                        <td>
                                            {outcome === 'accepted' ? (
                                                <span className={styles.chipAccepted}>
                                                    {STATUS_LABELS[row.status ?? ''] ?? row.status}
                                                    {row.is_test ? ' · test' : ''}
                                                </span>
                                            ) : (
                                                <span className={styles.chipRejected}>
                                                    {REASON_LABELS[row.reason_category ?? ''] ?? 'Rejected'}
                                                    {row.is_test ? ' · test' : ''}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <button className={styles.linkButton}>View</button>
                                        </td>
                                    </tr>
                                )
                            })}
                            {!listLoading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className={styles.emptyCell}>
                                        No deliveries found
                                        {applied.search.trim() ? ` for "${applied.search.trim()}"` : ''}.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className={styles.pagination}>
                <span>
                    {total} record{total === 1 ? '' : 's'}
                    {listLoading ? ' · loading…' : ''}
                </span>
                <div>
                    <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Previous
                    </button>
                    <span>
                        Page {page} of {totalPages}
                    </span>
                    <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                        Next
                    </button>
                </div>
            </div>

            {exporting && (
                <ExportModal
                    initialScope={filter}
                    includeTest={includeTest}
                    includeHistorical={includeHistorical}
                    onClose={() => setExporting(false)}
                />
            )}

            {detail && (
                <>
                    <div className={styles.slideOverBackdrop} onClick={closeRecord} />
                    <div className={styles.slideOver}>
                        <div className={styles.slideOverHeader}>
                            <h4>
                                {detail.view === 'accepted' ? 'Accepted delivery' : 'Rejected delivery'}
                                {record ? ` #${record.id}` : ''}
                            </h4>
                            <button className={styles.closeButton} onClick={closeRecord}>
                                Close
                            </button>
                        </div>
                        {detail.loading ? (
                            <p>Loading…</p>
                        ) : detail.error ? (
                            <p className={styles.errorText}>{detail.error}</p>
                        ) : detail.view === 'accepted' ? (
                            <AcceptedDetail data={detail.data} />
                        ) : (
                            <RejectedDetail data={detail.data} />
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

const Field = ({ label, value }: { label: string; value: any }) => (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <span className={styles.fieldValue}>{value == null || value === '' ? '—' : String(value)}</span>
    </div>
)

/** Stored signatures are usually data URLs; older/imported rows may be raw base64 PNG. */
const signatureImageSrc = (value: string | null | undefined): string | null => {
    if (!value || typeof value !== 'string') return null
    const trimmed = value.trim()
    if (!trimmed) return null
    if (trimmed.startsWith('data:')) return trimmed
    return `data:image/png;base64,${trimmed}`
}

const DocLink = ({
    url,
    s3Url,
}: {
    url: string | null
    s3Url?: string | null
}) => {
    const hasFFJ = Boolean(url)
    const hasS3 = Boolean(s3Url)
    if (!hasFFJ && !hasS3) return <span>—</span>
    return (
        <span className={styles.documentLinkIcons}>
            {hasFFJ ? (
                <a
                    className={styles.documentUrlIcon}
                    href={url!}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open FFJ request URL"
                    aria-label="Open FFJ request URL"
                >
                    <FaExternalLinkAlt aria-hidden="true" />
                </a>
            ) : (
                <span className={styles.documentUrlIconDisabled} title="No FFJ URL" aria-hidden="true">
                    <FaExternalLinkAlt />
                </span>
            )}
            {hasS3 ? (
                <a
                    className={styles.documentUrlIcon}
                    href={s3Url!}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open Sentinel S3 copy"
                    aria-label="Open Sentinel S3 copy"
                >
                    <FaCloud aria-hidden="true" />
                </a>
            ) : (
                <span className={styles.documentUrlIconDisabled} title="No S3 copy stored" aria-hidden="true">
                    <FaCloud />
                </span>
            )}
        </span>
    )
}

const documentSourceLabel = (source: string, doc?: { account_id?: string | null }): string => {
    if (source === 'agreement.documents') {
        return doc?.account_id ? `Agreement ${doc.account_id}` : 'Agreement'
    }
    if (source === 'enquiry.documents') return 'Enquiry (historical)'
    if (source === 'documents') return 'Top level'
    if (source === 'credit_report_pdf' || source === 'aml_pdf') return 'Client'
    return source
}

/** Agreements + document URLs from the raw FFJ payload, with download failures highlighted. */
const PayloadContents = ({
    payload,
    reasons,
    processingError,
    s3Documents,
    storedClaims,
}: {
    payload: any
    reasons?: string[] | null
    processingError?: string | null
    /** role → { url, key } for copies we stored in S3 (accepted deliveries). */
    s3Documents?: Record<string, any> | null
    storedClaims?: Array<{ case_ref?: string; policy_number?: string; base_claim?: number | string | boolean }> | null
}) => {
    const agreements = useMemo(() => extractPayloadAgreements(payload), [payload])
    const documents = useMemo(() => extractPayloadDocuments(payload), [payload])
    const failures = useMemo(
        () => extractDocumentFailures({ reasons, processingError }),
        [reasons, processingError]
    )

    const s3UrlForDoc = (doc: {
        role: string | null
        account_id?: string | null
        source: string
    }): string | null => {
        if (!doc.role || !s3Documents) return null
        const byCase = s3Documents.by_case_ref

        // Per-agreement rows: prefer that claim's stored copy via policy_number.
        if (doc.source === 'agreement.documents' && doc.account_id && Array.isArray(storedClaims)) {
            const claim = storedClaims.find(
                (c) => c.policy_number != null && String(c.policy_number) === String(doc.account_id)
            )
            if (claim?.case_ref && byCase?.[claim.case_ref]?.[doc.role]?.url) {
                return byCase[claim.case_ref][doc.role].url
            }
        }

        // Flat / historical top-level + enquiry docs: base claim copy.
        if (s3Documents[doc.role]?.url) return s3Documents[doc.role].url

        // Last resort: any claim that has this role stored.
        if (byCase && typeof byCase === 'object') {
            for (const roles of Object.values(byCase) as Array<Record<string, { url?: string }>>) {
                const url = roles?.[doc.role]?.url
                if (url) return url
            }
        }
        return null
    }

    if (!payload) {
        return <p className={styles.footnote}>Payload not available — cannot list agreements or document links.</p>
    }

    return (
        <>
            <h5 className={styles.sectionHeading}>Agreements in request ({agreements.length})</h5>
            {agreements.length ? (
                <table className={`${styles.dataTable} ${styles.agreementTable}`}>
                    <colgroup>
                        <col className={styles.agreementEnquiryColumn} />
                        <col className={styles.agreementLenderColumn} />
                        <col className={styles.agreementTypeColumn} />
                        <col className={styles.agreementStartColumn} />
                        <col className={styles.agreementContractColumn} />
                        <col className={styles.agreementAmountColumn} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Enquiry</th>
                            <th>Policy</th>
                            <th>Status</th>
                            <th>Start</th>
                            <th>Term</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agreements.map((a, i) => (
                            <tr key={`${a.account_id ?? 'agr'}-${i}`}>
                                <td>{a.enquiry_reference ?? '—'}</td>
                                <td>
                                    <span className={styles.tablePrimaryValue}>{a.lender_name ?? '—'}</span>
                                    <span className={styles.tableSecondaryValue}>
                                        Account: {a.account_id ?? '—'}
                                    </span>
                                </td>
                                <td>{a.status ?? '—'}</td>
                                <td>{formatDateUk(a.start_date)}</td>
                                <td>
                                    <span className={styles.tablePrimaryValue}>
                                        {a.loan_term != null ? `${a.loan_term} months` : '—'}
                                    </span>
                                    <span className={styles.tableSecondaryValue}>
                                        Monthly: {a.monthly_payment_amount ?? '—'}
                                    </span>
                                </td>
                                <td className={styles.tableNumericValue}>{a.original_amount ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className={styles.footnote}>No agreements found in the payload.</p>
            )}

            <h5 className={styles.sectionHeading}>Documents in request ({documents.length})</h5>
            <p className={styles.footnote}>
                Each claim must include its own CFA, LOA and AOF on <code>agreements[].documents[]</code>. Top-level{' '}
                <code>documents[]</code> accepts only AML and credit report on new deliveries (historical top-level /
                enquiry claim docs still show here). FFJ request URLs expire quickly — use the cloud icon for our
                stored S3 copy. Failed downloads are marked when this request was rejected or left docs-pending.
            </p>
            {documents.length ? (
                <table className={`${styles.dataTable} ${styles.documentTable}`}>
                    <colgroup>
                        <col className={styles.documentRoleColumn} />
                        <col className={styles.documentFilenameColumn} />
                        <col className={styles.documentSourceColumn} />
                        <col className={styles.documentLinkColumn} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Role</th>
                            <th>Filename</th>
                            <th>Source</th>
                            <th>Links</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((doc, i) => {
                            const failure = failureForDocument(doc, failures)
                            const roleLabel = doc.role
                                ? DOCUMENT_ROLE_LABELS[doc.role] ?? doc.role
                                : doc.type || 'Unrecognised'
                            const s3Url = s3UrlForDoc(doc)
                            return (
                                <tr key={`${doc.source}-${doc.account_id ?? ''}-${doc.filename ?? i}-${i}`}>
                                    <td>{roleLabel}</td>
                                    <td className={styles.documentFilename}>
                                        <span>{doc.filename ?? '—'}</span>
                                        {failure && (
                                            <span className={styles.documentFailure} title={failure.reason}>
                                                Download failed: {failure.reason}
                                            </span>
                                        )}
                                    </td>
                                    <td>{documentSourceLabel(doc.source, doc)}</td>
                                    <td className={styles.documentLinkCell}>
                                        <DocLink url={doc.url} s3Url={s3Url} />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            ) : (
                <p className={styles.footnote}>No documents found in the payload.</p>
            )}
        </>
    )
}

const AcceptedDetail = ({ data }: { data: any }) => {
    const { delivery, payload, stored } = data
    const client = stored?.client
    return (
        <div>
            <div className={styles.fieldGrid}>
                <Field label="Delivery id" value={delivery.delivery_id} />
                <Field label="Sender" value={delivery.sender_name} />
                <Field label="Submission ref" value={delivery.submission_reference} />
                <Field label="Status" value={STATUS_LABELS[delivery.status] ?? delivery.status} />
                <Field label="Received" value={formatDateTime(delivery.created_at)} />
                <Field label="Processed" value={formatDateTime(delivery.processed_at)} />
                <Field label="Agreements" value={delivery.agreement_count} />
                {delivery.error && <Field label="Processing error" value={delivery.error} />}
            </div>

            <PayloadContents
                payload={payload}
                processingError={delivery.error}
                s3Documents={stored?.s3_documents}
                storedClaims={stored?.claims}
            />

            <h5 className={styles.sectionHeading}>Stored client record</h5>
            {client ? (
                <div className={styles.fieldGrid}>
                    <Field label="Client id" value={client.id} />
                    <Field label="Name" value={`${client.title ?? ''} ${client.firstname ?? ''} ${client.lastname ?? ''}`.trim()} />
                    <Field label="DOB" value={client.dob} />
                    <Field label="Email" value={client.email} />
                    <Field label="Phone" value={client.telephone} />
                    <Field
                        label="Address"
                        value={[client.address_line_one, client.address_line_two, client.town_city, client.postcode]
                            .filter(Boolean)
                            .join(', ')}
                    />
                    <Field label="Opt-in" value={formatDateTime(client.opt_in_datetime)} />
                </div>
            ) : (
                <p className={styles.footnote}>No client row linked to this delivery.</p>
            )}

            <h5 className={styles.sectionHeading}>Stored claims ({stored?.claims?.length ?? 0})</h5>
            {stored?.claims?.length ? (
                <table className={`${styles.dataTable} ${styles.storedClaimsTable}`}>
                    <colgroup>
                        <col className={styles.storedClaimRefColumn} />
                        <col className={styles.storedClaimLenderColumn} />
                        <col className={styles.storedClaimStartColumn} />
                        <col className={styles.storedClaimContractColumn} />
                        <col className={styles.storedClaimAmountColumn} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Case ref</th>
                            <th>Policy</th>
                            <th>Start</th>
                            <th>Term</th>
                            <th>Loan value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stored.claims.map((c: any) => (
                            <tr key={c.id}>
                                <td>
                                    <span className={styles.tablePrimaryValue}>{c.case_ref}</span>
                                    {c.base_claim ? <span className={styles.baseClaimBadge}>Base claim</span> : null}
                                </td>
                                <td>
                                    <span className={styles.tablePrimaryValue}>{c.lender ?? '—'}</span>
                                    <span className={styles.tableSecondaryValue}>
                                        Account: {c.policy_number ?? '—'}
                                    </span>
                                </td>
                                <td>{formatDateUk(c.start_date)}</td>
                                <td>
                                    <span className={styles.tablePrimaryValue}>
                                        {c.length_of_contract != null ? `${c.length_of_contract} months` : '—'}
                                    </span>
                                    <span className={styles.tableSecondaryValue}>
                                        Monthly: {c.monthly_repayment ?? '—'}
                                    </span>
                                </td>
                                <td>
                                    <span className={styles.tablePrimaryValue}>
                                        {c.starting_balance ?? c.total_amount_payable ?? '—'}
                                    </span>
                                    <span className={styles.tableSecondaryValue}>
                                        Quantum: {c.quantum_value ?? '—'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className={styles.footnote}>No claims stored.</p>
            )}

            {stored?.signature_links?.length > 0 && (
                <>
                    <h5 className={styles.sectionHeading}>E-sign records</h5>
                    {stored.signature_links.map((s: any) => {
                        const signatureSrc = signatureImageSrc(s.signature)
                        return (
                            <div key={s.id} className={styles.signatureRecord}>
                                <div className={styles.fieldGrid}>
                                    <Field label="Signed at" value={formatDateTime(s.signature_date)} />
                                    <Field label="IP address" value={s.ip_address} />
                                    <Field label="Device / browser" value={s.device || s.browser} />
                                    <Field label="Case ref" value={s.case_ref} />
                                </div>
                                {signatureSrc ? (
                                    <div className={styles.signatureBox}>
                                        <img
                                            src={signatureSrc}
                                            alt="Client signature"
                                            className={styles.signatureImage}
                                        />
                                    </div>
                                ) : (
                                    <p className={styles.footnote}>No signature image stored for this record.</p>
                                )}
                            </div>
                        )
                    })}
                </>
            )}

            <JsonViewer title="Delivered payload (as received)" data={payload} emptyMessage="Payload not recorded" />
        </div>
    )
}

const RejectedDetail = ({ data }: { data: any }) => {
    const { rejection, payload, payload_error } = data
    return (
        <div>
            <div className={styles.fieldGrid}>
                <Field label="Delivery id" value={rejection.delivery_id} />
                <Field label="Sender" value={rejection.sender_name} />
                <Field label="Submission ref" value={rejection.submission_reference} />
                <Field label="Client ref" value={rejection.consumer_client_ref} />
                <Field label="Email" value={rejection.consumer_email} />
                <Field label="Received" value={formatDateTime(rejection.created_at)} />
                <Field label="Agreements" value={rejection.agreement_count} />
                <Field label="Category" value={REASON_LABELS[rejection.reason_category] ?? rejection.reason_category} />
                <Field label="S3 archive" value={rejection.s3_json_key} />
            </div>

            <h5 className={styles.sectionHeading}>Rejection reasons ({rejection.reasons?.length ?? 0})</h5>
            <ul className={styles.reasonList}>
                {(rejection.reasons ?? []).map((r: string, i: number) => (
                    <li key={i}>{r}</li>
                ))}
            </ul>

            <PayloadContents payload={payload} reasons={rejection.reasons} />

            <JsonViewer
                title="Raw JSON sent by FFJ"
                data={payload}
                emptyMessage={payload_error ?? 'Archived JSON unavailable'}
            />
        </div>
    )
}
